import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { KaimonoWebRoutes } from 'src/config/routes';
import { MIDDLE_CATEGORY_PAGE_PATH, SMALL_CATEGORY_PAGE_PATH, TINY_CATEGORY_PAGE_PATH } from 'src/constants/pagePath';
import type {
  LargeCategorySchema,
  MiddleCategorySchema,
  SmallCategorySchema,
  TinyCategorySchema
} from 'src/models/category/schema';
import { CategoryContext } from 'src/stores/Category';
import { Presenter } from './Presenter';

type Props = {
  categories: LargeCategorySchema[] | MiddleCategorySchema[] | SmallCategorySchema[] | TinyCategorySchema[];
  categoryType: 'largeCategory' | 'middleCategory' | 'smallCategory' | 'tinyCategory';
  activeCategorySlug: string;
  setActiveCategorySlug: React.Dispatch<React.SetStateAction<string>>;
};

export const CategoryList: React.FC<Props> = ({
  categories,
  categoryType,
  activeCategorySlug,
  setActiveCategorySlug
}) => {
  const [isCategoryListOpen, setIsCategoryListOpen] = useState(false);
  const { route, query, pathname } = useRouter();
  const category = useContext(CategoryContext);

  const getSmallOrTinyCategoryLink = (slug: string) => {
    // tinyカテゴリの有無によって遷移先を変更
    if (categoryType === 'smallCategory') return KaimonoWebRoutes.smallCategories.show(slug);
    return KaimonoWebRoutes.tinyCategories.show(slug);
  };

  const largeCategories = category.largeCategories.map((lc) => ({
    ...lc,
    middleCategories: lc.middleCategoryIds.map((id) => category.middleCategories.find((m) => m.middleCategoryId === id))
  }));
  const middleCategories = category.middleCategories.map((mc) => ({
    ...mc,
    smallCategories: mc.smallCategoryIds.map((id) => category.smallCategories.find((s) => s.smallCategoryId === id))
  }));
  const smallCategories = category.smallCategories.map((sc) => ({
    ...sc,
    tinyCategories: sc.tinyCategoryIds.map((id) => category.tinyCategories.find((t) => t.tinyCategoryId === id))
  }));

  const parentCategorySlug = (): string => {
    switch (pathname) {
      case MIDDLE_CATEGORY_PAGE_PATH:
        return largeCategories.find((lc) => lc.middleCategories.some((mc) => mc?.slug === query.slug))?.slug ?? '';
      case SMALL_CATEGORY_PAGE_PATH:
        return middleCategories.find((mc) => mc.smallCategories.some((sc) => sc?.slug === query.slug))?.slug ?? '';
      case TINY_CATEGORY_PAGE_PATH:
        return smallCategories.find((sc) => sc.tinyCategories.some((tc) => tc?.slug === query.slug))?.slug ?? '';
      default:
        return '';
    }
  };

  return (
    <Presenter
      pathname={pathname}
      route={route}
      categories={categories}
      isCategoryListOpen={isCategoryListOpen}
      setIsCategoryListOpen={setIsCategoryListOpen}
      parentCategorySlug={parentCategorySlug}
      getSmallOrTinyCategoryLink={getSmallOrTinyCategoryLink}
      setActiveCategorySlug={setActiveCategorySlug}
      categoryType={categoryType}
      activeCategorySlug={activeCategorySlug}
    />
  );
};
